/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React from 'react';
import { defineMessages } from 'react-intl';
import { Pill, PillProps } from 'src/core/components/Pill';
import { PillContainer } from 'src/core/components/PillContainer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { DocumentsFilter, FileFilter } from 'src/customText/appShell/dataTaggingPage/samplerStore';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type CtFiltersPillContainerProps = {
    maxDisplayNumber?: number;
    appliedFilters: DocumentsFilter;
    onChangeFilter: (filter: DocumentsFilter) => void;
};

const messages = defineMessages({
    untaggedFiles: {
        id: 'customText.ctFiltersPillContainer.untaggedFiles',
        defaultMessage: 'Unlabeled'
    },
    allTaggedFiles: {
        id: 'customText.ctFiltersPillContainer.allTaggedFiles',
        defaultMessage: 'Labeled'
    },
    autoTaggedFiles: {
        id: 'customText.ctFiltersPillContainer.autoTaggedFiles',
        defaultMessage: 'Auto-labeled'
    },
    trainingOnly: {
        id: 'customText.ctFiltersPillContainer.trainingOnly',
        defaultMessage: 'Training only'
    },
    testingOnly: {
        id: 'customText.ctFiltersPillContainer.testingOnly',
        defaultMessage: 'Testing only'
    },
    appliedAutoTagFilterTitle: {
        id: 'customText.ctFiltersPillContainer.appliedAutoTagFilterTitle',
        defaultMessage: 'Filtered with auto-labeled!'
    },
    appliedAutoTagFilterBody: {
        id: 'customText.ctFiltersPillContainer.appliedAutoTagFilterBody',
        defaultMessage: 'We have filtered your files with your auto-labels!'
    }
});

const theme = getScopedTheme('CtFiltersPillContainer');

const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: {
        border: theme.border,
        backgroundColor: theme.backgroundColor,
        fontSize: '12px',
        fontWeight: '400',
        height: '28.67px',
        minWidth: '30px',
        padding: 0
    },
    rootHovered: { backgroundColor: theme.hoveredBackgroundColor }
});

const getFileFilterText = (filter: FileFilter | string) => {
    switch (filter) {
        case 'tagged':
            return messages.allTaggedFiles;
        case 'autotagged':
            return messages.autoTaggedFiles;
        case 'training':
            return messages.trainingOnly;
        case 'testing':
            return messages.testingOnly;
        case 'untagged':
        default:
            return messages.untaggedFiles;
    }
};

/**
 * A component displaying the pills of the applied filters for Custom Text documents.
 */
export const CtFiltersPillContainer = (props: CtFiltersPillContainerProps) => {
    const { maxDisplayNumber, appliedFilters, onChangeFilter } = props;
    const { localize } = useLocalization();
    const areFiltersApplied = Boolean(appliedFilters.files.length || appliedFilters.labels.length);

    const onRemoveFilter = (filter: string) =>
        onChangeFilter({
            files: appliedFilters.files.filter(f => f !== filter),
            labels: appliedFilters.labels.filter(l => l.id !== filter)
        });

    const mapFilterToPill = (filter: FileFilter | string, index: number, name?: string) => ({
        index,
        name: name ?? filter,
        pillKey: filter,
        onRemovePill: () => onRemoveFilter(filter)
    });

    const allPills: PillProps[] = appliedFilters.files
        .map((f, index) => mapFilterToPill(f, index, localize(getFileFilterText(f))))
        .concat(...appliedFilters.labels.map((l, index) => mapFilterToPill(l.id, index, l.name)));

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextAppliedAutotagFilter'), []);

    React.useEffect(() => {
        if (appliedFilters.files.find(f => f === 'autotagged')) {
            createTeachingBubble(
                'CustomTextAppliedAutotagFilter',
                localize(messages.appliedAutoTagFilterBody),
                localize(messages.appliedAutoTagFilterTitle),
                `#ctFiltersPillContainer`,
                true,
                false
            );
        }
    }, [appliedFilters.files]);

    const collapsedFiltersPill = (
        <DefaultButton
            menuProps={{
                shouldFocusOnMount: true,
                styles: { root: { minWidth: '100px' } },
                items: allPills.slice(maxDisplayNumber).map(p => ({
                    key: p.pillKey as string,
                    text: p.name,
                    onRender: () => (
                        <Stack tokens={{ padding: '0 0 0 5px' }}>
                            <Pill {...p} />
                        </Stack>
                    )
                }))
            }}
            text={`+ ${allPills.length - maxDisplayNumber}`}
            styles={buttonStyles}
        />
    );

    const hideExtraFilters = Boolean(maxDisplayNumber && allPills.length > maxDisplayNumber);

    return (
        areFiltersApplied && (
            <Stack id="ctFiltersPillContainer" horizontal verticalAlign="center" wrap>
                <PillContainer allowAddPills={false} pills={hideExtraFilters ? allPills.slice(0, maxDisplayNumber) : allPills} />
                {hideExtraFilters && collapsedFiltersPill}
            </Stack>
        )
    );
};
